import React, { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import UsersTab from './users/UsersTab';
import TeamsTab from './teams/TeamsTab';
import { normaliseTeamsSubtab } from '@/lib/teamTypes';
import { InstallersTab } from './installers/InstallersTab';
import { User, Role, Team, Manager } from '@/types';
import { Installer } from './installers/InstallersTab';
import { useLocation, useNavigate } from 'react-router-dom';

interface DeletedUser {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  deleted_at: string;
  deleted_by: string | null;
  original_email: string;
  original_first_name: string;
  original_last_name: string;
  created_at: string;
}

interface UsersManagementTabProps {
  // Users props
  users: User[];
  deletedUsers: DeletedUser[];
  usersLoading: boolean;
  deletedUsersLoading: boolean;
  roles: Role[];
  teams: Team[];
  managers: Manager[];
  createUser: (user: Omit<User, 'id'>) => Promise<{ success: boolean }>;
  updateUser: (user: User) => Promise<{ success: boolean }>;
  deleteUser: (userId: string, isHardDelete?: boolean) => Promise<{ success: boolean }>;
  fetchDeletedUsers: () => void;
  fetchUsers: () => void;
  addUserError: string | null;
  onEmailChange: (email: string) => Promise<{ exists: boolean }>;
  onEmailCheck: (email: string) => Promise<boolean>;
  currentUserId?: string;
  
  // Teams props
  allTeams: Team[];
  teamsLoading: boolean;
  fetchTeams: () => void;
  createTeam: (teamData: Omit<Team, 'id'>) => Promise<void>;
  updateTeam: (teamId: string, updates: Partial<Team>) => Promise<void>;
  deleteTeam: (teamId: string) => Promise<void>;
  
  // Installers props
  installers: Installer[];
  installersLoading: boolean;
  createInstaller: (installerData: Omit<Installer, 'id' | 'created_at' | 'updated_at'>) => Promise<string>;
  updateInstaller: (installerData: Installer) => Promise<string>;
  deleteInstaller: (installerId: string) => Promise<void>;
  
  // Permissions
  canManageUsers: boolean;
  canManageTeams: boolean;
  canManageInstallers: boolean;
}

export const UsersManagementTab: React.FC<UsersManagementTabProps> = ({
  // Users props
  users,
  deletedUsers,
  usersLoading,
  deletedUsersLoading,
  roles,
  teams,
  managers,
  createUser,
  updateUser,
  deleteUser,
  fetchDeletedUsers,
  fetchUsers,
  addUserError,
  onEmailChange,
  onEmailCheck,
  currentUserId,
  
  // Teams props
  allTeams,
  teamsLoading,
  fetchTeams,
  createTeam,
  updateTeam,
  deleteTeam,
  
  // Installers props
  installers,
  installersLoading,
  createInstaller,
  updateInstaller,
  deleteInstaller,
  
  // Permissions
  canManageUsers,
  canManageTeams,
  canManageInstallers,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeSubTab, setActiveSubTab] = useState('users');
  
  // Parse subtab from URL on mount and when location changes
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    setActiveSubTab(normaliseTeamsSubtab(searchParams.get('subtab')));
  }, [location.search]);
  
  const handleSubTabChange = (value: string) => {
    setActiveSubTab(value);
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('subtab', value);
    navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true });
    
    if (value === 'teams' && allTeams.length === 0) {
      fetchTeams();
    }

    // Note: Deleted users are now fetched only when needed, not on every tab switch
  };
  
  const handleUpdateTeam = async (teamId: string, updates: Partial<Team>) => {
    if (!updates.name || updates.name.trim() === '') {
      throw new Error('Team name is required');
    }
    await updateTeam(teamId, {
      name: updates.name,
      type: updates.type || 'SALES',
      manager_id: updates.manager_id ?? null,
      biz_dev_id: updates.biz_dev_id ?? null,
    });
  };
  
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-foreground">User Management</h2>
        <p className="text-sm text-muted-foreground">Manage users, the teams they belong to, and the partner companies you work with</p>
      </div>
      
      <Tabs 
        value={activeSubTab} 
        onValueChange={handleSubTabChange}
        className="space-y-4"
      >
        <div className="flex items-center justify-between">
          <TabsList className="grid w-max grid-cols-3 bg-muted/50 border border-border h-10 p-0.5">
            {canManageUsers && (
              <TabsTrigger
                value="users"
                className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm !h-8 !px-3 !py-0 !my-0 !mx-0.5 !rounded-sm flex items-center justify-center"
              >
                Users
              </TabsTrigger>
            )}
            {canManageTeams && (
              <TabsTrigger
                value="teams"
                className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm !h-8 !px-3 !py-0 !my-0 !mx-0.5 !rounded-sm flex items-center justify-center"
              >
                Teams
              </TabsTrigger>
            )}
            {canManageInstallers && (
              <TabsTrigger
                value="partners"
                className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm !h-8 !px-3 !py-0 !my-0 !mx-0.5 !rounded-sm flex items-center justify-center"
              >
                Partners
              </TabsTrigger>
            )}
          </TabsList>
          
          {/* Action button that changes based on active tab */}
          {activeSubTab === 'users' && canManageUsers && (
            <Button 
              onClick={() => {
                // This will be handled by the UsersTab component
                const addUserButton = document.querySelector('[data-add-user-button]') as HTMLButtonElement;
                if (addUserButton) addUserButton.click();
              }}
              className="bg-primary hover:bg-primary/90 text-primary-foreground"
            >
              Add User
            </Button>
          )}
          {activeSubTab === 'teams' && canManageTeams && (
            <Button
              onClick={() => {
                // This will be handled by the TeamsTab component
                const addTeamButton = document.querySelector('[data-add-team-button]') as HTMLButtonElement;
                if (addTeamButton) addTeamButton.click();
              }}
              className="bg-primary hover:bg-primary/90 text-primary-foreground"
            >
              New team
            </Button>
          )}
          {activeSubTab === 'partners' && canManageInstallers && (
            <Button 
              onClick={() => {
                // This will be handled by the InstallersTab component
                const addInstallerButton = document.querySelector('[data-add-installer-button]') as HTMLButtonElement;
                if (addInstallerButton) addInstallerButton.click();
              }}
              className="bg-primary hover:bg-primary/90 text-primary-foreground"
            >
              New partner
            </Button>
          )}
        </div>
        
        {canManageUsers && (
          <TabsContent value="users" className="space-y-4">
            <UsersTab 
            users={users}
            deletedUsers={deletedUsers}
            roles={roles}
            teams={teams}
            managers={managers}
            isLoading={usersLoading}
            deletedUsersLoading={deletedUsersLoading}
            currentUserId={currentUserId}
            onCreateUser={createUser}
            onUpdateUser={updateUser}
            onDeleteUser={deleteUser}
            onRefreshDeletedUsers={fetchDeletedUsers}
            onRefreshUsers={fetchUsers}
            addUserError={addUserError}
            onEmailChange={onEmailChange}
            onEmailCheck={onEmailCheck}
          />
        </TabsContent>
      )}
      
      {canManageTeams && (
        <TabsContent value="teams" className="space-y-4">
          <TeamsTab
            teams={allTeams}
            managers={managers}
            roles={roles}
            isLoading={teamsLoading}
            onAddTeam={createTeam}
            onUpdateTeam={handleUpdateTeam}
            onDeleteTeam={deleteTeam}
          />
        </TabsContent>
      )}

      {canManageInstallers && (
        <TabsContent value="partners" className="space-y-4">
          <InstallersTab 
            installers={installers}
            managers={managers}
            isLoading={installersLoading}
            onAddInstaller={createInstaller}
            onUpdateInstaller={updateInstaller}
            onDeleteInstaller={deleteInstaller}
          />
        </TabsContent>
      )}
    </Tabs>
    </div>
  );
};